import { useMemo, useState } from "react"
import invariant from "tiny-invariant"
import {
  BusinessTypeEnum,
  SubscriptionBillingCycleEnum,
} from "~/__generated__/graphql"
import { useCurrentUser } from "~/auth/CurrentUserContext"
import { cn } from "~/common/shadcn-utils"
import { useSafeMutation } from "~/common/useSafeMutation"
import { useSubscriptionTiers } from "~/common/useSubscriptionTiers"
import { displayErrors } from "~/common/validations"
import check from "~/images/icons/check"
import ex from "~/images/icons/ex"
import { Button } from "~/shadcn/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/shadcn/ui/card"
import Text from "~/ui/typography"
import { moneyDisplay } from "~/util/numbers"
import { flushEvents, trackEvent } from "~/util/tracking"
import { CREATE_CHECKOUT_SESSION } from "../api"
import { BillingCycleToggle } from "./BillingCycleToggle"

const Checked = () => <img {...check} alt="Checked" />
const Unchecked = () => <img {...ex} alt="Unchecked" />

const displayPrice = (tier: {
  priceCents: number
  billingCycle: SubscriptionBillingCycleEnum
}) => {
  const divisor =
    tier.billingCycle === SubscriptionBillingCycleEnum.Monthly ? 1 : 12

  return `${moneyDisplay(tier.priceCents / divisor / 100)} / month`
}

export const SubscriptionPlanSelect = () => {
  const currentUser = useCurrentUser()
  const [billingCycle, setBillingCycle] = useState(
    SubscriptionBillingCycleEnum.Monthly
  )
  const { subscriptionTiers, loading, error } = useSubscriptionTiers(
    currentUser.agency?.businessType || BusinessTypeEnum.Talent
  )
  const filteredSubscriptionTiers = useMemo(() => {
    return subscriptionTiers.filter(
      (tier) => tier.billingCycle === billingCycle
    )
  }, [subscriptionTiers, billingCycle])

  const [createCheckoutSession] = useSafeMutation(CREATE_CHECKOUT_SESSION)

  const handlePlanSelection = async (tier: {
    key: string
    stripePriceId: string
    billingCycle: SubscriptionBillingCycleEnum
  }) => {
    const result = await createCheckoutSession({
      variables: { stripePriceId: tier.stripePriceId },
    })

    if (result.errors) {
      console.error("Errors creating checkout session:", result.errors)
      displayErrors(result.errors)
      return
    }

    invariant(result.data, "Expected data from createCheckoutSession")

    trackEvent("onboarding_step_completed", {
      step: "choose_plan",
      step_number: 3,
    })
    trackEvent("checkout_started", {
      tier: tier.key,
      billing_cycle: tier.billingCycle,
    })
    await flushEvents()

    window.location.href = result.data.checkoutSessionCreate.sessionUrl
  }

  if (loading) return <div>Loading...</div>
  if (error) return <div>Error: {error.message}</div>

  return (
    <>
      <div className="text-center flex flex-col mb-5">
        <Text variant="body2">
          <span className="font-bold">Beta Offer</span>: 40% off for early
          adopters
        </Text>
        <Text variant="body2">
          Join Zano during Beta and receive 40% off all plans for life as an
          early adopter and supporter of the platform.
        </Text>
        <Text variant="body4" className="mt-4">
          Limited availability | 30 day free trial included
        </Text>
      </div>
      <BillingCycleToggle
        billingCycle={billingCycle}
        setBillingCycle={setBillingCycle}
      />
      <div
        className={cn("grid grid-cols-1 md:grid-cols-3 gap-6", {
          "md:grid-cols-4": filteredSubscriptionTiers.length > 3,
        })}
      >
        {filteredSubscriptionTiers.map((product) => (
          <Card key={product.id} className="flex flex-1 flex-col">
            <CardHeader>
              <CardTitle>{product.displayName || product.name}</CardTitle>
              <CardDescription>{product.tagline}</CardDescription>
              <CardDescription>{displayPrice(product)}</CardDescription>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <ul className="mb-4">
                {!currentUser.hasBeenSubscribed && (
                  <li className="flex items-center mb-2 text-xs">
                    <Checked />
                    <span className="ml-2">30-day free trial</span>
                  </li>
                )}
                {product.features.map((feature, index) => (
                  <li key={index} className="flex items-center mb-2 text-xs">
                    {feature.included ? <Checked /> : <Unchecked />}
                    <span
                      className="ml-2"
                      dangerouslySetInnerHTML={{ __html: feature.description }}
                    />
                  </li>
                ))}
              </ul>
              <Button
                className="w-full mt-auto"
                onClick={() => handlePlanSelection(product)}
              >
                Go {product.name}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  )
}
